import { useEffect, useRef, type ReactNode } from 'react';
import { CloseIcon } from './Icons.js';

/**
 * A panel, not a page (wireframe §6a): the profile stays visible, dimmed,
 * while the officer chooses. Escape and the scrim close it; focus lands on
 * the panel when it opens.
 */
export function SidePanel({
  open,
  onClose,
  title,
  children,
  footer,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  labelledBy: string;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 animate-fade bg-scrim"
      />
      <aside
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="absolute inset-y-0 right-0 flex w-[460px] max-w-full animate-slide-in flex-col border-l border-line bg-surface shadow-panel outline-none"
      >
        <div className="flex items-start justify-between border-b border-line px-8 pt-8 pb-6">
          <div className="flex min-w-0 flex-col gap-1.5">{title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-btn border border-line hover:bg-sunk"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="flex grow flex-col gap-7 overflow-y-auto px-8 py-6">
          {children}
        </div>
        <div className="flex gap-3 border-t border-line px-8 pt-6 pb-8">{footer}</div>
      </aside>
    </div>
  );
}
